import { useEffect, useRef, useState } from "preact/hooks";
import { api, ApiError } from "../api";
import { playAcknowledgement, playAddTaskGreeting, playPeonLine } from "../audio";
import { navigate } from "../router";
import { PhotoPicker } from "../components/PhotoPicker";
import { ScreenTitle } from "../components/Shell";
import { BigJobToggle } from "../components/BigJobToggle";
import { useBountyUI, useEconomyUI, useProjectsUI } from "../features";
import { jobSizeLabel } from "../../shared/projects.ts";
import { setLastNotice } from "../award";
import { formatGold } from "../format";
import { LUXURY_TIERS, type LuxuryTier } from "../../shared/economy.ts";
import { BusyLabel } from "../components/BusyLabel";
import { boardAdded } from "./board-patch";
import { radioKeys } from "../radio-group.ts";
import { taskMissing, type Missing } from "../../shared/form-missing.ts";
import { MentionField } from "../components/MentionField";

/** `luxury` opens the form as a luxury task post; the tier is picked here. */
export function TaskForm({ peonName, luxury }: { peonName: string; luxury?: boolean }) {
  const economyUIEnabled = useEconomyUI();
  const bountyUIEnabled = useBountyUI();
  // Projects (docs/projects.md): with projectsUI on Big job reads "Project".
  const jobSize = jobSizeLabel(useProjectsUI());
  const luxuryMode = economyUIEnabled && !!luxury;
  const [tier, setTier] = useState<LuxuryTier>(LUXURY_TIERS[0]);
  const activeLuxury = luxuryMode ? tier : undefined;
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  // Notes hide behind "+ Add note" until asked for; a note with text always stays visible.
  const [notesOpen, setNotesOpen] = useState(false);
  const notesRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (notesOpen) notesRef.current?.focus();
  }, [notesOpen]);
  const [urgent, setUrgent] = useState(false);
  const [bigJob, setBigJob] = useState(false);
  const [photoId, setPhotoId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [balance, setBalance] = useState<number | null>(null);

  useEffect(() => {
    void playAddTaskGreeting();
  }, []);

  // Leaving with a title or photo entered (the back button or the browser's back) is abandoning the
  // task. Cancelling the photo check (Cancel or retake) only backs out of the check, so it plays no
  // line; only leaving the form does. Mirrors of the state, for the unmount cleanup.
  const titleRef = useRef(title);
  titleRef.current = title;
  const photoRef = useRef(photoId);
  photoRef.current = photoId;
  const submittedRef = useRef(false);
  const abandonedAtRef = useRef(0);
  const abandon = () => {
    // One line per exit.
    if (Date.now() - abandonedAtRef.current < 1500) return;
    abandonedAtRef.current = Date.now();
    void playPeonLine("abandon");
  };
  useEffect(
    () => () => {
      if (!submittedRef.current && (titleRef.current.trim() || photoRef.current)) abandon();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    if (!luxuryMode) return;
    let active = true;
    void api.rewards.summary(peonName).then(
      (s) => { if (active) setBalance(s.balance); },
      () => { if (active) setBalance(null); },
    );
    return () => { active = false; };
  }, [peonName, luxuryMode]);
  const canAffordTier = balance === null || balance >= tier;

  // Add task stays live while the form is incomplete (docs/ui-audit.md triage item 1): a press says
  // the first missing thing and puts the focus on it. The line goes once that field is filled.
  const formRef = useRef<HTMLFormElement>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const tiersRef = useRef<HTMLDivElement>(null);
  const [missing, setMissing] = useState<Missing | null>(null);
  const need = (photo: string | null) => taskMissing({ title, photoId: photo, canAffordTier: activeLuxury ? canAffordTier : true });
  const unready = need(photoId);
  useEffect(() => {
    // As an update: a press that set a new line before this effect ran keeps it.
    if (missing && unready?.field !== missing.field) setMissing((m) => (m && unready?.field !== m.field ? null : m));
  }, [missing, unready?.field]);
  function pointAt(m: Missing) {
    setError(null);
    setMissing(m);
    if (m.field === "title") titleInputRef.current?.focus();
    else if (m.field === "tier") tiersRef.current?.focus();
    else formRef.current?.querySelector<HTMLElement>(".photo-placeholder")?.focus();
  }

  async function submitWith(photo: string | null) {
    if (busy) return;
    // The before photo is the point of a task, so a task never posts without one; a luxury post the
    // balance cannot cover says so here instead of asking the server (the photo check's Add task too).
    const m = need(photo);
    if (m) {
      pointAt(m);
      return;
    }
    void playAcknowledgement();
    setBusy(true);
    setError(null);
    try {
      const created = await api.tasks.create({
        title: title.trim(),
        notes: notes.trim(),
        urgent: activeLuxury ? false : urgent,
        big_job: activeLuxury ? false : bigJob,
        luxury: activeLuxury,
        start_photo_id: photo,
        created_by: peonName,
      });
      submittedRef.current = true;
      // The board lands with the new card already on it; its own fetch then confirms it.
      boardAdded(created);
      void playPeonLine(activeLuxury ? "luxury" : "added");
      // The card lands at the bottom or in the pile, so the board says it arrived.
      setLastNotice("Task added");
      navigate({ name: "open" }, true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) setError("Not enough gold");
      else setError(err instanceof Error ? err.message : "Could not save");
    } finally {
      setBusy(false);
    }
  }

  // Urgent! and Big job are chosen on the "Looks good?" photo check. A luxury task's pay is fixed by
  // its tier, so urgency and job size do not apply to it.
  const jobToggles = activeLuxury ? null : (
    <>
      <label class={`field toggle urgent-toggle ${urgent ? "on" : ""}`}>
        <input
          type="checkbox"
          checked={urgent}
          onChange={(e) => {
            setUrgent(e.currentTarget.checked);
            if (e.currentTarget.checked) void playPeonLine("urgent");
          }}
        />
        <span>Urgent!</span>
      </label>
      <BigJobToggle
        label={jobSize}
        checked={bigJob}
        onChange={(on) => {
          setBigJob(on);
          if (on) void playPeonLine("bigjob");
        }}
        disabled={busy}
      />
    </>
  );

  return (
    <>
      <main class="content">
        <ScreenTitle title="New task" />
        {activeLuxury && (
          <div class="luxury-banner" ref={tiersRef} tabIndex={-1}>
            <p>Luxury task</p>
            <div class="segmented luxury-tiers" role="radiogroup" aria-label="Gold to put up" onKeyDown={radioKeys}>
              {LUXURY_TIERS.map((t) => (
                <button
                  key={t}
                  type="button"
                  class={tier === t ? "active" : ""}
                  role="radio"
                  aria-checked={tier === t}
                  disabled={busy || (balance !== null && balance < t)}
                  onClick={() => setTier(t)}
                >
                  {t}
                </button>
              ))}
            </div>
            <p class="muted small">{balance !== null ? `You have ${formatGold(balance)}` : "Loading your balance…"}</p>
          </div>
        )}
        <form
          class="form"
          ref={formRef}
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            void submitWith(photoId);
          }}
        >
          <label class="field">
            <span class="field-label">What needs doing?</span>
            <MentionField as="input" fieldRef={titleInputRef} pickerId="task-title" class="input" type="text" required maxLength={200} value={title} onValue={setTitle} autoFocus />
          </label>
          {notesOpen || notes ? (
            <label class="field">
              <span class="field-label">Note</span>
              <MentionField as="textarea" fieldRef={notesRef} pickerId="task-notes" class="input" rows={3} maxLength={4000} value={notes} onValue={setNotes} />
            </label>
          ) : (
            <button type="button" class="btn btn-ghost add-note" onClick={() => setNotesOpen(true)}>
              + Add note
            </button>
          )}
          <PhotoPicker
            label="Before photo"
            allowClear={false}
            photoId={photoId}
            onChange={setPhotoId}
            confirmLabel={title.trim() ? "Add task" : "Use photo"}
            keepLabel={title.trim() ? "Keep editing" : undefined}
            checkExtras={jobToggles}
            onConfirm={(id) => {
              // Title already filled in: the confirmed photo is the last step, so submit right away.
              if (title.trim()) void submitWith(id);
            }}
          />
          {/* After "Use photo" the check's two choices stay in sight as small pills under the photo,
              so they can be changed without taking the photo again. */}
          {photoId && !activeLuxury && (
            <div class="task-flag-pills" role="group" aria-label="This task">
              <button
                type="button"
                class={`task-flag-pill task-flag-urgent${urgent ? " on" : ""}`}
                aria-pressed={urgent}
                disabled={busy}
                onClick={() => {
                  setUrgent(!urgent);
                  if (!urgent) void playPeonLine("urgent");
                }}
              >
                <span class="pill-check" aria-hidden="true" />
                Urgent
              </button>
              {bountyUIEnabled && (
                <button
                  type="button"
                  class={`task-flag-pill task-flag-big${bigJob ? " on" : ""}`}
                  aria-pressed={bigJob}
                  disabled={busy}
                  onClick={() => {
                    setBigJob(!bigJob);
                    if (!bigJob) void playPeonLine("bigjob");
                  }}
                >
                  <span class="pill-check" aria-hidden="true" />
                  {jobSize}
                </button>
              )}
            </div>
          )}
          {missing ? (
            <div class="form-error" role="alert" id="task-missing">{missing.line}</div>
          ) : (
            error && <div class="form-error" role="alert">{error}</div>
          )}
          <button
            class={`btn btn-primary btn-block${unready ? " btn-unready" : ""}`}
            type="submit"
            disabled={busy}
            aria-describedby={missing ? "task-missing" : undefined}
          >
            <BusyLabel busy={busy} label="Saving…">{activeLuxury ? `Post for ${formatGold(activeLuxury)}` : "Add task"}</BusyLabel>
          </button>
          <p class="muted small posting-as">Posting as {peonName}</p>
        </form>
      </main>
    </>
  );
}
